import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { BooleanParam, useQueryParam, withDefault } from 'use-query-params';
import useAsyncEffect from 'use-async-effect';
import { Experience, ExperienceStatus } from '@/webapi/use-experience-api';
import { SummaryTable } from '@/features/details/summary';
import { ResultCharts } from '@/features/details/charts';
import { useOptionsModals } from '@/features/dashboard/experiences/use-options-modals';
import {
  DatePickerContext,
  newDatePickerContext,
} from '@/features/details/date-pick/context';
import {
  SummaryContext,
  SummaryProvider,
} from '@/features/details/summary/context';
import { StatisticalSignificance } from '@/features/details/sample-size/StatisticalSignificance';
import { getStatusForScheduledExperien } from '@/utils/experience-utils';
import { ScheduledChart } from '@/features/details/charts/scheduled-chart';
import { ControlsStrip } from '@/features/details/experiment-controls';
import { SourceReport } from '@/features/details/utm-source';
import { MoreMetrics } from '@/features/details/charts/more-metrics';
import { VSpace } from '@/components/spacing';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { WinnerUplift } from '@/features/details/winner-uplift';
import { breakpoints, isMobile } from '@/components/responsive';
import { MobileSummaryTable } from '@/features/details/summary/mobile-summary-row';
import { getMetricsStartDate } from '@/features/details/shared';
import {
  PriceTestReports,
  PriceTestSwitch,
} from '@/features/details/price-test-reports';
import { useStoreSettingsApi } from '@/webapi/use-store-settings-api';

export interface ExperienceDetailsProps {
  experience?: Experience;
  setExperience?: (e: Experience) => void;
}

function ExperienceReports({
  currentExperienceStatus,
  experience,
  setExperience,
}: {
  experience: Experience;
  setExperience: (e: Experience) => void;
  currentExperienceStatus: ExperienceStatus;
}) {
  const isUpliftStatsEnabled = useFeatureBit(FeatureBit.UPLIFT_METRICS);
  const schedule = experience?.schedule;
  const isMobileDevice = isMobile();
  const status = experience?.status;
  const isTraficReportEnabled = showTrafficSourceReport(
    experience.metricsStartAt,
  );
  const { showPriceTestReport } = useContext(SummaryContext);
  if (showPriceTestReport) {
    return null;
  }

  const [shouldShowCI, setShouldShowCI] = useQueryParam<boolean>(
    `showCI`,
    withDefault(BooleanParam, false),
  );
  const { getUserSettings } = useStoreSettingsApi();

  useAsyncEffect(async () => {
    if (!isMobileDevice || shouldShowCI) return;
    const userSettings = await getUserSettings();
    setShouldShowCI(userSettings?.isConfidenceIntervalsEnabled === `YES`);
  }, []);

  const ciEnabled = useMemo(
    () => useFeatureBit(FeatureBit.CONFIDENCE_INTERVALS) && shouldShowCI,
    [shouldShowCI],
  );

  return (
    <>
      <StatisticalSignificance
        experience={experience}
        setExperience={setExperience}
      />
      {isUpliftStatsEnabled && !isMobileDevice && (
        <WinnerUplift experience={experience} />
      )}
      {isMobileDevice ? (
        <MobileSummaryTable ciEnabled={ciEnabled} />
      ) : (
        <SummaryTable
          experience={experience}
          status={status}
          setExperience={setExperience}
          ciEnabled={ciEnabled}
        />
      )}
      {currentExperienceStatus === ExperienceStatus.SCHEDULED && (
        <ScheduledChart schedule={schedule} />
      )}
      <ResultCharts experience={experience} setExperience={setExperience} />
      {!isMobileDevice && isTraficReportEnabled && (
        <>
          <VSpace value={2} />
          <SourceReport experience={experience} />
        </>
      )}
      <VSpace value={5} />
      <MoreMetrics experience={experience} setExperience={setExperience} />
    </>
  );
}

export function ExperimentLoadedDetails({
  experience,
  setExperience,
}: ExperienceDetailsProps) {
  const {
    archive: { ArchiveModal, archiveModalProps, showArchiveModal, archiveRef },
    pause: { PauseModal, pauseModalProps, showPauseModal, pauseRef },
  } = useOptionsModals({ experience });

  const datePickerContext = newDatePickerContext(experience);
  const currentExperienceStatus = getStatusForScheduledExperien(experience);

  return (
    <DatePickerContext.Provider value={datePickerContext}>
      <SummaryProvider experience={experience}>
        <Wrapper>
          <ArchiveModal {...archiveModalProps} />
          <PauseModal {...pauseModalProps} />
          <div />
          <ControlsStrip
            experience={experience}
            currentExperienceStatus={currentExperienceStatus}
            setExperience={setExperience}
            showPauseModal={showPauseModal}
            showArchiveModal={showArchiveModal}
            pauseRef={pauseRef}
            archiveRef={archiveRef}
          />
          <PriceTestSwitch />
          <ExperienceReports
            experience={experience}
            setExperience={setExperience}
            currentExperienceStatus={currentExperienceStatus}
          />
          <PriceTestReports
            experience={experience}
            setExperience={setExperience}
          />
        </Wrapper>
      </SummaryProvider>
    </DatePickerContext.Provider>
  );
}

const Wrapper = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  position: relative;
  display: flex;
  flex-direction: column;

  ${breakpoints.down(`md`)} {
    max-width: 100vw;
    overflow-x: hidden;
    margin: 0;
  }
`;

export function calcStartDate(
  dateFromQueryParam: Date,
  experienceStartDate: Date | undefined,
  metricsStartAt: string,
) {
  return dateFromQueryParam || new Date(experienceStartDate || metricsStartAt);
}
const showTrafficSourceReport = (metricStartsAt?: string) => {
  const availableSince = new Date(`2024-10-20T00:00:00.000Z`).valueOf();
  const start = getMetricsStartDate();

  return (
    metricStartsAt &&
    new Date(metricStartsAt).valueOf() > availableSince &&
    start?.valueOf() > availableSince
  );
};
